import React from "react";
import { Vendor } from "@/components/common/SelectVendors";
import { VendorProfile } from "@/__generated__/graphql";
import Avatar from "@/components/common/Avatar";
import LongContentWrapper from "@/components/common/LongContentWrapper";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { find } from "lodash";
import { MapPin, Plus, Minus, X } from "lucide-react";

type Props = {
  vendors: Vendor[];
  selectedVendors: Vendor[];
  onVendorSelect: (vendor: Vendor) => void;
  onVendorDeselect: (vendor: Vendor) => void;
  onNext: () => void;
  onBack: () => void;
  limit: number;
};

const SelectVendorsInline: React.FC<Props> = ({
  vendors,
  selectedVendors,
  onVendorSelect,
  onVendorDeselect,
  onNext,
  onBack,
  limit,
}) => {
  const filteredVendors = vendors.filter(
    (vendor) => vendor.profile.__typename === "VendorProfile",
  );

  return (
    <div className="flex flex-col h-auto max-h-[600px]">
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <div className="grid grid-cols-1 gap-4">
            {filteredVendors.map((vendor) => {
              const isSelected = vendor
                ? !!find(selectedVendors, (v) => v.id === vendor.id)
                : false;
              const profile = vendor.profile;
              const office = vendor.offices.length > 0 && vendor.offices[0];
              const country = office && office.country && office.country.name;

              return (
                <div
                  key={vendor.id}
                  className={cn(
                    "flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors",
                    isSelected
                      ? "border-service-paint bg-service-paint/5"
                      : "border-border hover:border-service-paint/50",
                  )}
                  onClick={() => {
                    if (isSelected) {
                      onVendorDeselect(vendor);
                    } else if (selectedVendors.length < limit) {
                      onVendorSelect(vendor);
                    }
                  }}
                >
                  <div className="flex items-center gap-4">
                    <Avatar
                      name={
                        profile?.__typename === "VendorProfile"
                          ? (profile as VendorProfile).companyName
                          : ""
                      }
                      url={
                        profile?.__typename === "VendorProfile"
                          ? (profile as VendorProfile).logoUrl
                          : ""
                      }
                      avatarSize={40}
                      className="rounded-md"
                    />

                    <div className="flex-1">
                      <div className="font-medium text-foreground">
                        <LongContentWrapper>
                          {profile?.__typename === "VendorProfile"
                            ? (profile as VendorProfile).companyName
                            : ""}
                        </LongContentWrapper>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <MapPin className="text-muted-foreground" size={14} />
                        <span className="text-sm text-muted-foreground">
                          {country}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    className={cn(
                      "p-2 rounded-sm border",
                      isSelected
                        ? "border-service-paint bg-service-paint text-white"
                        : "border-border bg-background text-muted-foreground",
                    )}
                  >
                    {isSelected ? <Minus size={16} /> : <Plus size={16} />}
                  </div>
                </div>
              );
            })}
          </div>

          {!filteredVendors.length && (
            <div className="text-center py-8 text-muted-foreground">
              {"No vendors available."}
            </div>
          )}
        </div>
      </div>

      <Separator className="my-4" />

      <div className="flex-shrink-0 ">
        {selectedVendors.length > 0 && (
          <div className="mb-4">
            <div className="text-sm font-medium text-foreground mb-2">
              Selected Vendors ({selectedVendors.length}/{limit})
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedVendors.map((vendor) => (
                <div
                  key={vendor.id}
                  className="flex items-center gap-2 px-3 py-1 bg-service-paint/10 border border-service-paint/20 rounded-full text-sm"
                >
                  <span className="text-foreground">
                    {vendor.profile?.__typename === "VendorProfile"
                      ? (vendor.profile as VendorProfile).companyName
                      : ""}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onVendorDeselect(vendor);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={onNext}
            disabled={selectedVendors.length === 0}
            className="bg-service-paint hover:bg-service-paint/90 text-white"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SelectVendorsInline;
